import { TypedDocumentNode, gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { So5LeaderboardRarity } from '@sorare/core/src/__generated__/globalTypes';
import { Info } from '@sorare/core/src/atoms/icons/Info';
import { User } from '@sorare/core/src/atoms/icons/User';
import {
  Cell,
  CellLink,
  Line,
  SortableTableHeader,
} from '@sorare/core/src/atoms/layout/Table';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { DivisionLogo } from '@sorare/core/src/components/leaderboard/DivisionLogo';
import {
  MLB_COMPOSE_TEAM_LEADERBOARDSLUG,
  MLB_COMPOSE_TEAM_LEADERBOARDSLUG_LINEUPID,
  MLB_PLAY_PRO_FIXTURE_COMPETITIONS_LEADERBOARD,
  MLB_PLAY_PRO_FIXTURE_MY_LINEUPS_LINEUP,
} from '@sorare/core/src/constants/__generated__/routes';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { rewardsConfigToRewardsOverview } from '@sorare/core/src/lib/rewards';

import { messages } from '@sorare/gameplay/src/components/lobby/Leaderboards/messages';
import { LineupActions } from '@sorare/gameplay/src/components/lobby/LineupActions';
import { AnyRewardConfig } from '@sorare/gameplay/src/components/rewards/AnyRewardConfig';
import { isFixtureOpened } from '@sorare/gameplay/src/lib/so5';

import { MajorsHighlight } from './MajorsHighlight';
import type { UpcomingLeaderboards_So5Leaderboard } from './__generated__/upcoming.graphql';
import {
  Areas,
  Grey,
  LeaderboardName,
  NumberWithUnit,
  StyledLink,
  TableStyled,
  TooltipWrapper,
  Wrapper,
} from './shared';

const RewardOverviewWrapper = styled(Horizontal)`
  flex-wrap: wrap;
  & p {
    font: var(--t-label-s);
  }
`;

type Props = {
  globalLeaderboards?: UpcomingLeaderboards_So5Leaderboard[];
  onDelete: (lineupId: string) => void;
};

const LineWrapper = styled(Vertical).attrs({ gap: 0 })``;

const ActionCell = ({
  leaderboard,
  lineupId,
  onDelete,
}: {
  leaderboard: UpcomingLeaderboards_So5Leaderboard;
  lineupId: string;
  onDelete: (lineupId: string) => void;
}) => {
  return (
    <Cell area={Areas.cta} align="right">
      <LinkOther as="div">
        <LineupActions
          leaderboardDisplayName={leaderboard.displayName}
          leaderboardRarity={
            leaderboard.rarityType.toUpperCase() as So5LeaderboardRarity
          }
          isUserEligible={
            !leaderboard?.canCompose?.notEnoughEligibleCards ||
            !!leaderboard?.composeBuy
          }
          composeUrl={generatePath(MLB_COMPOSE_TEAM_LEADERBOARDSLUG, {
            leaderboardSlug: leaderboard.slug,
          })}
          isTraining={leaderboard.trainingCenter}
          nbOfLineups={leaderboard.mySo5Lineups.length}
          teamsCap={leaderboard.teamsCap}
          lineupId={leaderboard.mySo5Lineups[0]?.id}
          editUrl={generatePath(MLB_COMPOSE_TEAM_LEADERBOARDSLUG_LINEUPID, {
            leaderboardSlug: leaderboard.slug,
            lineupId: idFromObject(lineupId) || '',
          })}
          onDelete={onDelete}
        />
      </LinkOther>
    </Cell>
  );
};

export const UpcomingLeaderboards = ({
  onDelete,
  globalLeaderboards,
}: Props) => {
  const { formatMessage, formatNumber } = useIntl();

  const leaderboards = globalLeaderboards || [];

  const header: SortableTableHeader<UpcomingLeaderboards_So5Leaderboard> = [
    { id: 'logo' },
    {
      id: 'name',
      cell: formatMessage(messages.tabsName),
    },
    { id: 'badge' },
    {
      id: 'rewards',
      cell: formatMessage(messages.tabsRewards),
    },
    {
      id: 'players',
      cell: formatMessage(messages.tabsParticipants),
      align: 'right',
    },
    { id: 'cta', align: 'right' },
  ];

  const filteredAndSortedLeaderboards = leaderboards;

  return (
    <Wrapper>
      <TableStyled
        rows={{
          header,
          body: (
            <>
              {filteredAndSortedLeaderboards.map(leaderboard => {
                const { so5LineupsCount: lineupsCount } = leaderboard;
                const lineupId = leaderboard.mySo5Lineups[0]?.id;
                const linkProps = {
                  to: lineupId
                    ? generatePath(MLB_PLAY_PRO_FIXTURE_MY_LINEUPS_LINEUP, {
                        lineup: idFromObject(lineupId),
                        fixture: leaderboard.so5Fixture.slug,
                      })
                    : generatePath(
                        MLB_PLAY_PRO_FIXTURE_COMPETITIONS_LEADERBOARD,
                        {
                          leaderboard: leaderboard.slug,
                          fixture: leaderboard.so5Fixture.slug,
                        }
                      ),
                };
                const chaseCumulativeLeaderboard =
                  leaderboard.universalSo5UserGroups?.[0];

                const rewardsOverview = rewardsConfigToRewardsOverview(
                  chaseCumulativeLeaderboard
                    ? chaseCumulativeLeaderboard.totalRewardConfigs
                    : leaderboard.totalRewardConfigs
                );
                return (
                  <LineWrapper key={leaderboard.slug}>
                    <Line aria-label={leaderboard.displayName}>
                      <Cell area={Areas.logo}>
                        <DivisionLogo so5Leaderboard={leaderboard} />
                      </Cell>
                      <Cell area={Areas.name}>
                        <LinkOverlay as={StyledLink} {...linkProps}>
                          <Horizontal>
                            <LeaderboardName>
                              {leaderboard.displayName}
                            </LeaderboardName>
                            {!!leaderboard.description && (
                              <TooltipWrapper>
                                <LinkOther
                                  as={Tooltip}
                                  title={leaderboard.description}
                                >
                                  <button tabIndex={0} type="button">
                                    <Info />
                                  </button>
                                </LinkOther>
                              </TooltipWrapper>
                            )}
                          </Horizontal>
                        </LinkOverlay>
                      </Cell>
                      <Cell area={Areas.rewards}>
                        <CellLink as={Link} {...linkProps}>
                          <RewardOverviewWrapper>
                            {rewardsOverview.map(rewardConfig => {
                              return (
                                <AnyRewardConfig
                                  key={rewardConfig.id}
                                  anyRewardConfig={rewardConfig}
                                />
                              );
                            })}
                          </RewardOverviewWrapper>
                        </CellLink>
                      </Cell>
                      <Cell
                        area={Areas.participants}
                        style={{ marginLeft: 'auto' }}
                      >
                        <Horizontal>
                          <NumberWithUnit>
                            <Grey>
                              <User />
                            </Grey>
                            {formatNumber(lineupsCount, {
                              notation: 'compact',
                            })}
                          </NumberWithUnit>
                        </Horizontal>
                      </Cell>
                      <Cell showOnDesktop area={Areas.badge}>
                        {leaderboard.mySo5Lineups.length > 0 && (
                          <Chip size="large">
                            {formatMessage({
                              id: 'LobbyUpcoming.UpcomingLeaderboards.Chip.Joined',
                              defaultMessage: 'Joined',
                            })}
                          </Chip>
                        )}
                      </Cell>
                      {isFixtureOpened(leaderboard.so5Fixture) && (
                        <ActionCell
                          leaderboard={leaderboard}
                          lineupId={lineupId}
                          onDelete={onDelete}
                        />
                      )}
                    </Line>
                    {leaderboard.displayName === 'Majors' && (
                      <MajorsHighlight />
                    )}
                  </LineWrapper>
                );
              })}
            </>
          ),
        }}
      />
    </Wrapper>
  );
};

UpcomingLeaderboards.fragments = {
  So5Leaderboard: gql`
    fragment UpcomingLeaderboards_So5Leaderboard on So5Leaderboard {
      slug
      displayName
      svgLogoUrl
      so5LineupsCount
      description
      rarityType
      so5Fixture {
        slug
        aasmState
      }
      trainingCenter
      teamsCap
      mySo5Lineups {
        id
      }
      composeBuy
      canCompose {
        notEnoughEligibleCards
        reason
      }
      mainRarityType
      totalRewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ...DivisionLogo_so5Leaderboard
      universalSo5UserGroups {
        slug
        totalRewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
      }
    }
    ${DivisionLogo.fragments.so5Leaderboard}
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<UpcomingLeaderboards_So5Leaderboard>,
};
